/**
 * App.jsx — the screen-flow STATE MACHINE.
 *
 * Owns: a nav STACK, the shared flow state, the boot sequence, native
 * BackButton wiring, and theme reactivity. Screen bodies live in src/screens/*
 * (written in the next phase) and receive a fixed prop contract — see
 * SCREEN_PROPS below.
 *
 * ── SCREEN ENUM ────────────────────────────────────────────────────────────
 *   BOOT     S0   — splash while /session resolves (App-owned; no component)
 *   NOT_LINKED S0a — "register in the bot" notice (shown when /session ->
 *                    NOT_PAIRED); PIN pairing is retired — linking happens in
 *                    the bot via phone-share before the app is opened
 *   CONSENT  S0b  — one-time biometric consent
 *   ENROLL   S0c  — one-time face enrolment
 *   LANDING  S1   — daily home; status hero + the single next action
 *   BRANCH   S2   — branch pick (only for a 2-branch worker)
 *   LOCATION S3   — geofence (LocationManager; desktop -> DESKTOP state)
 *   LIVE     S4   — IN-APP live getUserMedia capture (mesh + oval + action cue);
 *                   auto-falls back to the browser hand-off on Android/cam fail
 *   FACE     S4*  — legacy file-input capture (kept, no longer in the flow)
 *   RESULT   S5   — success seal (kirish / chiqish)
 *   HISTORY  S6   — read-only "Mening davomatim" (tiles + calendar)
 *   DAY      S7   — one-day bottom sheet (overlay on HISTORY)
 *
 * ── FLOW ───────────────────────────────────────────────────────────────────
 *   S0 → [S0a NOT_LINKED?] → [S0b CONSENT?] → [S0c ENROL?] → S1 LANDING
 *   S1 → (S2 BRANCH?) → S3 LOCATION → S4 LIVE → S5 RESULT ↺ S1
 *        (LIVE auto-hands-off to the browser /capture page if in-app cam fails)
 *   S1 → S6 HISTORY → S7 DAY ↺ S6
 *   ( ) = conditional  ·  native BackButton pops the stack
 *
 * ── PER-SCREEN PROP CONTRACT (SCREEN_PROPS) ────────────────────────────────
 * Every screen component receives ONE props object:
 *   {
 *     ctx      — derived read-model: { name, branches, consent, enrolled,
 *                today:{status,check_in,check_out}, colorScheme, user }
 *     api      — lib/api.js (pair/session/consent/enroll/challenge/checkin/
 *                checkout/summary/history) — init_data auto-attached
 *     tg       — lib/tg.js wrapper (MainButton/BackButton/haptic/getLocation/…)
 *     nav      — { push(screen,params), pop(), replace(screen,params),
 *                  reset(screen,params), reboot(), stackDepth, screen }
 *     state    — shared flow state (see SHARED_STATE)
 *     setState — merge-setter: setState(partial)
 *     params   — params passed to THIS nav entry
 *   }
 *
 * ── SHARED FLOW STATE (state / setState) ───────────────────────────────────
 *   {
 *     session   — last /session result (name, branches, consent, enrolled, today)
 *     direction — 'in' | 'out'   (the punch the worker chose on LANDING)
 *     branchId  — number | null  (chosen branch, when 2-branch)
 *     geo       — { lat, lng, accuracy } | null   (from LOCATION)
 *     image_b64 — string | null  (captured face still, data-prefix stripped)
 *     cue       — 'turn_left'|'turn_right'|'look_up'|'look_down' | null  (server-issued, for FACE)
 *     result    — { direction, time, branch } | null  (for RESULT)
 *     faceFails — number  (LIVENESS_FAIL/FACE_MISMATCH count; ≥3 → manager)
 *   }
 *
 * Per-screen params the flow passes (documented for the screens phase):
 *   NOT_LINKED {}                                → open bot / re-check session
 *   CONSENT  {}                                  → on ok: route enrol/landing
 *   ENROLL   {}                                  → on ok: replace LANDING
 *   LANDING  {}                                  → push BRANCH|LOCATION with direction
 *   BRANCH   { direction }                       → push LOCATION { direction, branch_id }
 *   LOCATION { direction, branch_id? }           → push FACE { direction, branch_id?, geo }
 *   FACE     { direction, branch_id?, geo }      → replace RESULT { ... } (or error)
 *   RESULT   { direction, time, branch }         → nav.reboot() back to LANDING
 *   HISTORY  {}                                  → push DAY { day }
 *   DAY      { day }                             → pop
 */
import { useCallback, useEffect, useReducer, useState } from 'react';

import * as tg from './lib/tg.js';
import api from './lib/api.js';
import { errorUI } from './lib/errors.js';

import Screen from './components/Screen.jsx';
import Spinner from './components/Spinner.jsx';
import ErrorState from './components/ErrorState.jsx';
import DaySheet from './components/DaySheet.jsx';

import BootScreen from './screens/BootScreen.jsx';
import CaptureScreen from './screens/CaptureScreen.jsx';
import NotLinkedScreen from './screens/NotLinkedScreen.jsx';
import ConsentScreen from './screens/ConsentScreen.jsx';
import EnrollScreen from './screens/EnrollScreen.jsx';
import LandingScreen from './screens/LandingScreen.jsx';
import BranchScreen from './screens/BranchScreen.jsx';
import LocationScreen from './screens/LocationScreen.jsx';
import FaceScreen from './screens/FaceScreen.jsx';
import InAppLiveCapture from './screens/InAppLiveCapture.jsx';
import ResultScreen from './screens/ResultScreen.jsx';
import HistoryScreen from './screens/HistoryScreen.jsx';

// ---- screen enum ------------------------------------------------------------
export const SCREENS = {
  BOOT: 'BOOT',
  NOT_LINKED: 'NOT_LINKED', // shown on NOT_PAIRED — worker must link in the bot
  CONSENT: 'CONSENT',
  ENROLL: 'ENROLL',
  LANDING: 'LANDING',
  BRANCH: 'BRANCH',
  LOCATION: 'LOCATION',
  FACE: 'FACE',        // legacy file-input capture (kept as a manual fallback)
  LIVE: 'LIVE',        // in-app live getUserMedia capture (primary S4)
  RESULT: 'RESULT',
  HISTORY: 'HISTORY',
  DAY: 'DAY',
};

// map screen id -> component (BOOT/DAY are handled specially in render)
const SCREEN_COMPONENT = {
  [SCREENS.NOT_LINKED]: NotLinkedScreen,
  [SCREENS.CONSENT]: ConsentScreen,
  [SCREENS.ENROLL]: EnrollScreen,
  [SCREENS.LANDING]: LandingScreen,
  [SCREENS.BRANCH]: BranchScreen,
  [SCREENS.LOCATION]: LocationScreen,
  [SCREENS.FACE]: FaceScreen,
  [SCREENS.LIVE]: InAppLiveCapture,
  [SCREENS.RESULT]: ResultScreen,
  [SCREENS.HISTORY]: HistoryScreen,
};

const INITIAL_STATE = {
  session: null,
  direction: null,
  branchId: null,
  geo: null,
  image_b64: null,
  cue: null,
  result: null,
  faceFails: 0,
};

function sharedReducer(state, partial) {
  if (partial === '@reset') return { ...INITIAL_STATE };
  return { ...state, ...partial };
}

/**
 * Given a paired-session-ish object ({consent, enrolled}), pick the first
 * screen the worker still needs before LANDING.
 */
function routeAfterAuth({ consent, enrolled }) {
  if (!consent) return SCREENS.CONSENT;
  if (!enrolled) return SCREENS.ENROLL;
  return SCREENS.LANDING;
}

// True for the whole life of the page — the pathname never changes without a
// full reload — so this constant-per-load branch keeps hook order stable below.
const IS_CAPTURE =
  typeof window !== 'undefined' &&
  window.location.pathname.replace(/\/+$/, '').endsWith('/capture');

export default function App() {
  // BROWSER-MODE hand-off page: /capture runs the real-browser camera flow
  // (getUserMedia + MediaPipe) authed purely by the ?t= token — no Telegram,
  // no initData, none of the Mini App nav stack / boot below.
  if (IS_CAPTURE) {
    return <CaptureScreen />;
  }

  // nav stack of { screen, params }
  const [stack, setStack] = useState([{ screen: SCREENS.BOOT, params: {} }]);
  const [shared, setShared] = useReducer(sharedReducer, INITIAL_STATE);
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState(null); // error code or null
  const [, forceTick] = useState(0); // theme repaint

  const setState = useCallback((partial) => setShared(partial), []);

  // ---- nav ops --------------------------------------------------------------
  const push = useCallback((screen, params = {}) => {
    setStack((s) => [...s, { screen, params }]);
  }, []);
  const pop = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);
  const replace = useCallback((screen, params = {}) => {
    setStack((s) => [...s.slice(0, -1), { screen, params }]);
  }, []);
  const reset = useCallback((screen, params = {}) => {
    setStack([{ screen, params }]);
  }, []);

  // ---- boot: /session -> route ---------------------------------------------
  const runBoot = useCallback(async () => {
    setBooting(true);
    setBootError(null);
    const res = await api.session();
    if (res.ok) {
      setShared({ session: res });
      reset(routeAfterAuth(res));
    } else if (res.error === 'NOT_PAIRED') {
      // Linking now happens in the bot (phone share) — don't ask for a PIN,
      // point the worker back to the bot to finish the one-time onboarding.
      reset(SCREENS.NOT_LINKED);
    } else {
      // AUTH_FAILED / NETWORK / SERVER_ERROR — fatal splash-level error
      setBootError(res.error || 'SERVER_ERROR');
    }
    setBooting(false);
  }, [reset]);

  const reboot = useCallback(async () => {
    setShared('@reset');
    await runBoot();
  }, [runBoot]);

  useEffect(() => {
    runBoot();
  }, [runBoot]);

  // ---- BackButton (register once; show/hide on depth) -----------------------
  useEffect(() => {
    const off = tg.BackButton.onClick(pop);
    return off;
  }, [pop]);

  useEffect(() => {
    if (stack.length > 1) tg.BackButton.show();
    else tg.BackButton.hide();
  }, [stack.length]);

  // ---- theme reactivity -----------------------------------------------------
  useEffect(() => {
    const off = tg.onThemeChanged(() => {
      tg.applyTheme();
      forceTick((n) => n + 1);
    });
    return off;
  }, []);

  // ---- assemble the nav object handed to screens ----------------------------
  const top = stack[stack.length - 1];
  const nav = {
    push,
    pop,
    replace,
    reset,
    reboot,
    stackDepth: stack.length,
    screen: top.screen,
  };

  // derived read-model
  const s = shared.session || {};
  const ctx = {
    name: s.name || tg.user?.first_name || '',
    branches: s.branches || [],
    consent: !!s.consent,
    enrolled: !!s.enrolled,
    today: s.today || { status: 'none', check_in: null, check_out: null },
    colorScheme: tg.colorScheme(),
    user: tg.user,
  };

  const propsFor = (entry) => ({
    ctx,
    api,
    tg,
    nav,
    state: shared,
    setState,
    params: entry.params || {},
  });

  // ---- render ---------------------------------------------------------------
  if (booting) {
    return (
      <Screen>
        <BootScreen {...propsFor(top)} />
      </Screen>
    );
  }

  if (bootError) {
    const home = errorUI(bootError).recover === 'settings' ? 'settings' : 'home';
    return (
      <Screen>
        <ErrorState
          code={bootError}
          onRecover={() => (home === 'settings' ? tg.openLocationSettings() : reboot())}
        />
      </Screen>
    );
  }

  // S7 DAY is an overlay: keep HISTORY (stable key) mounted underneath.
  if (top.screen === SCREENS.DAY) {
    const below = stack[stack.length - 2] || { screen: SCREENS.HISTORY, params: {} };
    return (
      <>
        <HistoryScreen key="history" {...propsFor(below)} />
        <DaySheet day={top.params?.day} onClose={pop} />
      </>
    );
  }

  const Cmp = SCREEN_COMPONENT[top.screen] || LandingScreen;
  return <Cmp key={top.screen} {...propsFor(top)} />;
}
